import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  DialogActions,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Typography,
  Alert,
} from "@mui/material";
import { useFetchPersonByDNI, usePersonForm } from "../../../hooks/usePersons";
import { useTaxiServicesTypes } from "../../../hooks/administration/useTaxiServicesTypes";
import { useAuthContext } from "../../../context/AuthProvider";
import axiosInstance from "../../../api/axiosInstance";

// Esquema de validación Yup con soporte para el nuevo modo
const schema = yup.object({
  perid: yup.number().typeError("Debe buscar y seleccionar un pasajero válido").required("El pasajero es requerido."),
  pernom: yup.string().required("El nombre es obligatorio."),
  perape: yup.string().required("El apellido es obligatorio."),
  ordserdetdes: yup.string().nullable(),
  perdni: yup.string().matches(/^\d{8}$/, "Debe tener 8 dígitos").required("El DNI es obligatorio."),
  ordserdetpunsubid: yup.number().typeError("Seleccione un punto de subida válido").required("Punto de subida requerido"),
  ordserdetpunbajid: yup.number().typeError("Seleccione un punto de bajada válido").required("Punto de bajada requerido"),
  ordserdettaxser: yup.boolean().nullable(),
  ordserdettra: yup.string().oneOf(["IDA", "VUELTA"]).required(),
  taxtipid: yup.number().nullable().when('ordserdettaxser', {
    is: true,
    then: () => yup.number().typeError("Seleccione tipo de vehículo").required("El tipo de taxi es requerido"),
    otherwise: () => yup.number().nullable()
  }),
  // taxlocid es obligatorio solo cuando es taxi y el modo es Última milla
  // Para ello usamos un campo extra 'taxiMode' que no se envía al backend
  taxiMode: yup.string().oneOf(["ULTIMA_MILLA", "ENTRE_PUNTOS"]),
  taxlocid: yup.number().nullable().when(['ordserdettaxser', 'taxiMode'], {
    is: (ordserdettaxser, taxiMode) => ordserdettaxser === true && taxiMode === "ULTIMA_MILLA",
    then: () => yup.number().typeError("Seleccione dirección de destino").required("La dirección es requerida"),
    otherwise: () => yup.number().nullable()
  })
});

const TravelerForm = ({
  open,
  handleClose,
  onSubmit,
  defaultValues = {},
  listTraveler,
  ordenServicioId,
  paradas,
  addMutation,
  updateMutation,
  service
}) => {
  const isEditing = Boolean(defaultValues?.ordserdetid);
  const { user } = useAuthContext();

  // Determinar si la naturaleza de la vista es una ruta de Taxi
  const isTaxiServiceType = useMemo(() => Number(service?.tipserid) === 3, [service]);

  const [dni, setDni] = useState("");
  const [searchDni, setSearchDni] = useState(null);
  const [dniDuplicated, setDniDuplicated] = useState(false);
  const [dniErrorMessage, setDniErrorMessage] = useState("");
  const [personNotFound, setPersonNotFound] = useState(false);
  const [personFound, setPersonFound] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [availablePersons, setAvailablePersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);

  const [activePersonId, setActivePersonId] = useState(null);
  const [personLocations, setPersonLocations] = useState([]);

  // Estado para controlar el modo de taxi (UI)
  const [taxiMode, setTaxiMode] = useState("ULTIMA_MILLA");
  // Rol dentro del modo Última milla (ORIGEN o DESTINO)
  const [taxPersonLocationRole, setTaxPersonLocationRole] = useState("DESTINO");

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "onChange",
  });

  const watchTaxSer = watch("ordserdettaxser");
  const watchTaxiMode = watch("taxiMode");
  const isSaving = Boolean(addMutation?.isPending || updateMutation?.isPending);

  const { taxiServicesTypes: taxiTypes } = useTaxiServicesTypes();
  const { data: traveler, isLoading, refetch } = useFetchPersonByDNI(searchDni, { enabled: false });
  const { personDetail, isLoadingDetail } = usePersonForm(activePersonId);

  // Sincronizar residencias de la persona en edición
  useEffect(() => {
    if (personDetail?.direcciones) {
      setPersonLocations(personDetail.direcciones);

      // Solo asignamos taxlocid si estamos en modo Última milla
      if (!isEditing && personDetail.direcciones.length > 0 && watchTaxiMode === "ULTIMA_MILLA") {
        const mainLoc = personDetail.direcciones.find(loc => loc.perlocpri || loc.PerLocPri);
        const targetLocId = mainLoc ? mainLoc.locid : personDetail.direcciones[0].locid;

        setValue("taxlocid", targetLocId);

        if (isTaxiServiceType) {
          if (taxPersonLocationRole === "ORIGEN") {
            setValue("ordserdetpunsubid", targetLocId);
          } else {
            setValue("ordserdetpunbajid", targetLocId);
          }
        }
      }
    } else {
      setPersonLocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personDetail, isEditing, setValue, isTaxiServiceType, taxPersonLocationRole, watchTaxiMode]);

  // Inicialización al abrir el formulario
  useEffect(() => {
    if (open) {
      // Determinar el modo inicial según si existe taxlocid
      const hasTaxLocId = Boolean(defaultValues?.taxlocid);
      const initialMode = isTaxiServiceType ? (hasTaxLocId ? "ULTIMA_MILLA" : "ENTRE_PUNTOS") : "ULTIMA_MILLA";
      setTaxiMode(initialMode);

      const cleaned = {
        perid: defaultValues?.perid || null,
        pernom: defaultValues?.pernom || "",
        perape: defaultValues?.perape || "",
        perdni: defaultValues?.perdni || "",
        ordserdetdes: defaultValues?.ordserdetdes || "",
        ordserdetpunsubid: defaultValues?.ordserdetpunsubid || null,
        ordserdetpunbajid: defaultValues?.ordserdetpunbajid || null,
        ordserdettaxser: isTaxiServiceType
          ? true
          : defaultValues?.ordserdettaxser || false,
        ordserdettra: isTaxiServiceType ? "IDA" : defaultValues?.ordserdettra || "IDA",
        ordserdetestpas: defaultValues?.ordserdetestpas || "NO_ASIGNADO",
        taxtipid: isTaxiServiceType
          ? defaultValues?.taxtipid || 3
          : defaultValues?.taxtipid || null,
        taxlocid: isTaxiServiceType && initialMode === "ULTIMA_MILLA"
          ? defaultValues?.taxlocid || null
          : null,
        taxiMode: initialMode, // campo solo para UI
      };

      reset(cleaned);

      // Determinar el rol (ORIGEN/DESTINO) según dónde coincida taxlocid
      if (isTaxiServiceType && initialMode === "ULTIMA_MILLA" && cleaned.taxlocid) {
        const taxLocationId = Number(cleaned.taxlocid);
        if (Number(cleaned.ordserdetpunsubid) === taxLocationId) {
          setTaxPersonLocationRole("ORIGEN");
        } else {
          setTaxPersonLocationRole("DESTINO");
        }
      } else {
        setTaxPersonLocationRole("DESTINO");
      }

      setDni(cleaned.perdni);
      setPersonFound(Boolean(cleaned.perid));
      setPersonNotFound(false);
      setDniDuplicated(false);
      setSearchDni(null);
      setActivePersonId(cleaned.perid || null);
    }
  }, [open, reset, defaultValues, isTaxiServiceType]);

  // Búsqueda por DNI
  useEffect(() => {
    if (searchDni) {
      refetch().then(({ data }) => {
        if (data) {
          setPersonFound(true);
          setPersonNotFound(false);
          setValue("perid", data.perid);
          setValue("pernom", data.pernom);
          setValue("perape", data.perape);
          setValue("perdni", data.perdni);
          setActivePersonId(data.perid);
        } else {
          setPersonNotFound(true);
          setPersonFound(false);
          setValue("perid", null);
          setValue("pernom", "");
          setValue("perape", "");
          setActivePersonId(null);
        }
      });
    }
  }, [searchDni, refetch, setValue]);

  const handleDniSearch = () => {
    if (dni.length === 8) {
      const currentTramo = getValues("ordserdettra");
      const exists = listTraveler?.some(
        (v) => v.perdni === dni && v.ordserdettra === currentTramo && v.ordserdetid !== defaultValues?.ordserdetid
      );

      if (exists) {
        setDniDuplicated(true);
        setDniErrorMessage("Este pasajero ya fue agregado para este tramo.");
        setPersonNotFound(false);
        setPersonFound(false);
      } else {
        setDniDuplicated(false);
        setDniErrorMessage("");
        setSearchDni(dni);
      }
    }
  };

  // Buscador predictivo por nombres
  useEffect(() => {
    if (searchText.trim().length < 2) {
      setAvailablePersons([]);
      return;
    }
    const controller = new AbortController();
    setLoadingPersons(true);

    const delayDebounce = setTimeout(() => {
      axiosInstance
        .get('/persons/basic/search2', {
          params: { search: searchText.trim() },
          signal: controller.signal,
        })
        .then(res => setAvailablePersons(res.data ?? []))
        .catch(error => {
          if (error.name !== 'CanceledError') console.error(error);
        })
        .finally(() => setLoadingPersons(false));
    }, 400);

    return () => {
      clearTimeout(delayDebounce);
      controller.abort();
    };
  }, [searchText]);

  // Manejar cambio de modo de taxi
  const handleTaxiModeChange = (newMode) => {
    setTaxiMode(newMode);
    setValue("taxiMode", newMode);
    // Limpiar campos que no correspondan
    if (newMode === "ENTRE_PUNTOS") {
      // Limpiar taxlocid y los campos de subida/bajada que puedan haber quedado del modo anterior
      setValue("taxlocid", null);
      // No limpiar ordserdetpunsubid/bajid porque en entre puntos se seleccionan de paradas
      // pero si había valores previos de última milla, podrían estar en punsubid o punbajid,
      // los dejamos porque el usuario los cambiará manualmente.
    } else {
      // Al cambiar a última milla, limpiar los puntos de subida/bajada para evitar conflictos
      setValue("ordserdetpunsubid", null);
      setValue("ordserdetpunbajid", null);
      // Si hay direcciones del pasajero, asignar una por defecto
      if (personLocations.length > 0) {
        const mainLoc = personLocations.find(loc => loc.perlocpri);
        const targetLocId = mainLoc ? mainLoc.locid : personLocations[0].locid;
        setValue("taxlocid", targetLocId);
        // Asignar también el punto opuesto según el rol actual
        if (taxPersonLocationRole === "ORIGEN") {
          setValue("ordserdetpunsubid", targetLocId);
        } else {
          setValue("ordserdetpunbajid", targetLocId);
        }
      }
    }
  };

  const handleFormExecution = () => {
    handleSubmit((formData) => {
      const isUltimaMilla = formData.taxiMode === "ULTIMA_MILLA";
      const passengerPayload = {
        ordsercabid: Number(ordenServicioId),
        perid: formData.perid,
        ordserdetdes: formData.ordserdetdes || null,
        ordserdetpunsubid: Number(formData.ordserdetpunsubid),
        ordserdetpunbajid: Number(formData.ordserdetpunbajid),
        ordserdettaxser: isTaxiServiceType ? false : (formData.ordserdettaxser || false),
        ordserdetestpas: formData.ordserdetestpas || "NO_ASIGNADO",
        ordserdettra: formData.ordserdettra || "IDA",
        taxtipid: isTaxiServiceType ? Number(formData.taxtipid) : (formData.ordserdettaxser ? Number(formData.taxtipid) : null),
        // taxlocid se envía solo si es última milla, de lo contrario null
        taxlocid: isTaxiServiceType && isUltimaMilla ? Number(formData.taxlocid) : null,
      };
      onSubmit(passengerPayload);
    })();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{isEditing ? "Editar Pasajero" : "Adicionar Pasajero de Última Milla"}</DialogTitle>
      <form onSubmit={(e) => e.preventDefault()}>
        <DialogContent>
          {isTaxiServiceType && (
            <Alert severity="info" sx={{ mb: 2, fontWeight: 700 }}>
              Modo Taxi de Última Milla: El punto de subida está fijado en: <b>{service?.taxi_punto_transbordo_nom || "Cargando..."}</b>
            </Alert>
          )}

          <Box display="flex" alignItems="center" gap={2} width="100%">            
            {!isTaxiServiceType && (
              <Controller
                name="ordserdettra"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Tramo"
                    fullWidth
                    margin="normal"
                  >
                    <MenuItem value="IDA">SUBIDA</MenuItem>
                    <MenuItem value="VUELTA">BAJADA</MenuItem>
                  </TextField>
                )}
              />
            )}

            <Controller
              name="perdni"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="DNI"
                  fullWidth
                  margin="normal"
                  value={dni}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setDni(e.target.value);
                    setDniDuplicated(false);
                    setPersonNotFound(false);
                  }}
                  error={dniDuplicated || personNotFound || !!errors.perdni}
                  helperText={errors.perdni?.message || (dniDuplicated ? dniErrorMessage : personNotFound ? 'El DNI no está registrado' : '')}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Button variant="contained" color="primary" onClick={handleDniSearch} disabled={isLoading || dni.length !== 8} sx={{ height: 55, mt: 1 }}>
              Buscar DNI
            </Button>
          </Box>

          {!isEditing && (
            <Box display="flex" width="100%" mt={1}>
              <Autocomplete
                options={availablePersons}
                loading={loadingPersons}
                fullWidth
                onChange={(e, newValue) => {
                  if (newValue) {
                    setDni(newValue.perdni ?? '');
                    setValue('perdni', newValue.perdni ?? '');
                    setValue('perid', newValue.perid);
                    setValue('pernom', newValue.pernom);
                    setValue('perape', newValue.perape);
                    setPersonFound(true);
                    setDniDuplicated(false);
                    setPersonNotFound(false);
                    setActivePersonId(newValue.perid);
                  }
                }}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason === 'reset') return;
                  setSearchText(newInputValue ?? '');
                }}
                getOptionLabel={(option) => `${option.pernom ?? ''} ${option.perape ?? ''} (${option.perdni ?? ''})`.trim()}
                renderInput={(params) => <TextField {...params} label="Buscar Persona por Nombre" margin="normal" fullWidth error={!!errors.perid} />}
              />
            </Box>
          )}

          <Box display="flex" gap={2}>
            <Controller
              name="pernom"
              control={control}
              render={({ field }) => <TextField {...field} label="Nombre" fullWidth margin="normal" disabled InputLabelProps={{ shrink: true }} />}
            />
            <Controller
              name="perape"
              control={control}
              render={({ field }) => <TextField {...field} label="Apellido" fullWidth margin="normal" disabled InputLabelProps={{ shrink: true }} />}
            />
          </Box>

          <Controller
            name="ordserdetdes"
            control={control}
            render={({ field }) => <TextField {...field} label="Observaciones" fullWidth margin="normal" />}
          />

          {/* Modo No-Taxi: puntos de subida y bajada */}
          {!isTaxiServiceType && (
            <Box display="flex" gap={2}>
              <Controller
                name="ordserdetpunsubid"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Punto de Subida"
                    fullWidth
                    margin="normal"
                    value={field.value ?? ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      field.onChange(value === "" ? null : Number(value));
                    }}
                    error={!!errors.ordserdetpunsubid}
                    helperText={errors.ordserdetpunsubid?.message}
                  >
                    <MenuItem value="">Seleccione punto de subida...</MenuItem>
                    {(paradas || []).map((location) => (
                      <MenuItem key={location.locid} value={Number(location.locid)}>
                        {location.locnom}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="ordserdetpunbajid"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Punto de Bajada"
                    fullWidth
                    margin="normal"
                    value={field.value ?? ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      field.onChange(value === "" ? null : Number(value));
                    }}
                    error={!!errors.ordserdetpunbajid}
                    helperText={errors.ordserdetpunbajid?.message}
                  >
                    <MenuItem value="">Seleccione punto de bajada...</MenuItem>
                    {(paradas || []).map((location) => (
                      <MenuItem key={location.locid} value={Number(location.locid)}>
                        {location.locnom}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>
          )}

          {/* Bloque específico para servicios TAXI */}
          {isTaxiServiceType && (
            <>
              {/* Selector de modo de taxi */}
              <Box display="flex" gap={2} mt={2}>
                <TextField
                  select
                  label="Tipo de servicio de taxi"
                  fullWidth
                  value={taxiMode}
                  onChange={(e) => handleTaxiModeChange(e.target.value)}
                >
                  <MenuItem value="ULTIMA_MILLA">Última milla (domicilio ↔ punto)</MenuItem>
                  <MenuItem value="ENTRE_PUNTOS">Traslado entre puntos (punto ↔ punto)</MenuItem>
                </TextField>
              </Box>

              {/* Modo Última milla */}
              {taxiMode === "ULTIMA_MILLA" && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px dashed",
                    borderColor: "primary.main",
                    borderRadius: 2,
                    bgcolor: "#f4f9ff",
                  }}
                >
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Configuración de última milla
                  </Typography>

                  <Box display="flex" gap={2} mt={1}>
                    <TextField
                      select
                      label="Recorrido requerido"
                      fullWidth
                      value={taxPersonLocationRole}
                      onChange={(event) => {
                        const newRole = event.target.value;
                        const passengerLocationId = getValues("taxlocid");

                        setTaxPersonLocationRole(newRole);

                        // Limpiar ambos extremos
                        setValue("ordserdetpunsubid", null, { shouldValidate: true });
                        setValue("ordserdetpunbajid", null, { shouldValidate: true });

                        if (passengerLocationId) {
                          if (newRole === "ORIGEN") {
                            setValue("ordserdetpunsubid", Number(passengerLocationId), { shouldValidate: true });
                          } else {
                            setValue("ordserdetpunbajid", Number(passengerLocationId), { shouldValidate: true });
                          }
                        }
                      }}
                    >
                      <MenuItem value="ORIGEN">Domicilio del pasajero → Localización</MenuItem>
                      <MenuItem value="DESTINO">Localización → Domicilio del pasajero</MenuItem>
                    </TextField>

                    <Controller
                      name="taxtipid"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Tipo de Vehículo Taxi"
                          fullWidth
                          value={field.value || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(value === "" ? null : Number(value));
                          }}
                          error={!!errors.taxtipid}
                          helperText={errors.taxtipid?.message}
                        >
                          <MenuItem value="">Seleccione capacidad...</MenuItem>
                          {(taxiTypes || []).map((type) => (
                            <MenuItem key={type.taxtipid} value={Number(type.taxtipid)}>
                              {type.taxtipnom} (Capacidad: {type.taxtipcan})
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box display="flex" gap={2} mt={2}>
                    <Controller
                      name="taxlocid"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label={
                            taxPersonLocationRole === "ORIGEN"
                              ? "Domicilio de origen"
                              : "Domicilio de destino"
                          }
                          fullWidth
                          disabled={isLoadingDetail}
                          value={field.value || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            const locationId = value === "" ? null : Number(value);
                            field.onChange(locationId);

                            if (taxPersonLocationRole === "ORIGEN") {
                              setValue("ordserdetpunsubid", locationId, { shouldValidate: true });
                            } else {
                              setValue("ordserdetpunbajid", locationId, { shouldValidate: true });
                            }
                          }}
                          error={!!errors.taxlocid}
                          helperText={
                            isLoadingDetail
                              ? "Obteniendo direcciones..."
                              : errors.taxlocid?.message
                          }
                        >
                          <MenuItem value="">Seleccione residencia...</MenuItem>
                          {personLocations.map((location) => (
                            <MenuItem key={location.locid} value={Number(location.locid)}>
                              {location.perloctip} - {location.locnom}
                              {location.perlocpri ? " ⭐ Principal" : ""}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />

                    <Controller
                      name={
                        taxPersonLocationRole === "ORIGEN"
                          ? "ordserdetpunbajid"
                          : "ordserdetpunsubid"
                      }
                      control={control}
                      render={({ field }) => {
                        const isPassengerOrigin = taxPersonLocationRole === "ORIGEN";
                        const fieldError = isPassengerOrigin
                          ? errors.ordserdetpunbajid
                          : errors.ordserdetpunsubid;

                        return (
                          <TextField
                            {...field}
                            select
                            label={
                              isPassengerOrigin
                                ? "Localización de destino"
                                : "Localización de origen"
                            }
                            fullWidth
                            value={field.value || ""}
                            onChange={(event) => {
                              const value = event.target.value;
                              field.onChange(value === "" ? null : Number(value));
                            }}
                            error={!!fieldError}
                            helperText={fieldError?.message}
                          >
                            <MenuItem value="">Seleccione localización...</MenuItem>
                            {(paradas || []).map((location) => (
                              <MenuItem key={location.locid} value={Number(location.locid)}>
                                {location.locnom}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Modo Traslado entre puntos */}
              {taxiMode === "ENTRE_PUNTOS" && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px dashed",
                    borderColor: "secondary.main",
                    borderRadius: 2,
                    bgcolor: "#f9f4ff",
                  }}
                >
                  <Typography variant="subtitle2" color="secondary" gutterBottom>
                    Traslado entre puntos (sin domicilio)
                  </Typography>

                  <Box display="flex" gap={2} mt={1}>
                    <Controller
                      name="taxtipid"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Tipo de Vehículo Taxi"
                          fullWidth
                          value={field.value || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(value === "" ? null : Number(value));
                          }}
                          error={!!errors.taxtipid}
                          helperText={errors.taxtipid?.message}
                        >
                          <MenuItem value="">Seleccione capacidad...</MenuItem>
                          {(taxiTypes || []).map((type) => (
                            <MenuItem key={type.taxtipid} value={Number(type.taxtipid)}>
                              {type.taxtipnom} (Capacidad: {type.taxtipcan})
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box display="flex" gap={2} mt={2}>
                    <Controller
                      name="ordserdetpunsubid"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Punto de Subida"
                          fullWidth
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(value === "" ? null : Number(value));
                          }}
                          error={!!errors.ordserdetpunsubid}
                          helperText={errors.ordserdetpunsubid?.message}
                        >
                          <MenuItem value="">Seleccione punto de subida...</MenuItem>
                          {(paradas || []).map((location) => (
                            <MenuItem key={location.locid} value={Number(location.locid)}>
                              {location.locnom}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />

                    <Controller
                      name="ordserdetpunbajid"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Punto de Bajada"
                          fullWidth
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(value === "" ? null : Number(value));
                          }}
                          error={!!errors.ordserdetpunbajid}
                          helperText={errors.ordserdetpunbajid?.message}
                        >
                          <MenuItem value="">Seleccione punto de bajada...</MenuItem>
                          {(paradas || []).map((location) => (
                            <MenuItem key={location.locid} value={Number(location.locid)}>
                              {location.locnom}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="button" variant="contained" onClick={handleFormExecution} disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} /> : isEditing ? "Actualizar Cambios" : "Adicionar Viajero"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TravelerForm;